import React, { useEffect, useState } from 'react';
import { evaluationService } from '../../services/evaluationService';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
    PieChart, Pie, Cell, LineChart, Line, Legend
} from 'recharts';
import { AlertTriangle, TrendingUp, CheckCircle, BarChart3 } from 'lucide-react';

const COLORS = ['#6366f1', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#f43f5e'];
const LEVEL_COLORS: Record<string, string> = {
    'NIVEL_I': '#10b981',
    'NIVEL_II': '#f59e0b',
    'NIVEL_III': '#ef4444'
};

const EvaluationAnalytics: React.FC = () => {
    const [data, setData] = useState<any>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadAnalytics();
    }, []);

    const loadAnalytics = async () => {
        try {
            const patterns = await evaluationService.getPatterns();
            setData(patterns);
        } catch (error) {
            console.error('Error loading analytics:', error);
        } finally {
            setLoading(false);
        }
    };

    if (loading) return (
        <div className="flex flex-col items-center justify-center py-24 space-y-4">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div>
            <p className="text-slate-400 font-medium animate-pulse">Analizando patrones de coordinación interinstitucional...</p>
        </div>
    );

    const hasData = data && data.averageCategoryScores && Object.keys(data.averageCategoryScores).length > 0;

    if (!hasData) {
        return (
            <div className="bg-slate-800/30 border border-slate-700/50 rounded-3xl p-16 text-center backdrop-blur-sm">
                <div className="bg-slate-800/80 w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6 border border-slate-700">
                    <BarChart3 className="w-10 h-10 text-slate-500" />
                </div>
                <h3 className="text-2xl font-bold text-slate-200">Reconocimiento de Patrones</h3>
                <p className="text-slate-500 mt-3 max-w-md mx-auto leading-relaxed">
                    El sistema de inteligencia requiere una base histórica de evaluaciones para identificar tendencias y fallas estructurales.
                </p>
                <div className="mt-8 inline-flex items-center gap-2 px-4 py-2 bg-indigo-500/10 border border-indigo-500/20 rounded-full text-indigo-400 text-sm">
                    <CheckCircle className="w-4 h-4" />
                    Complete al menos 2 evaluaciones para activar el análisis
                </div>
            </div>
        );
    }

    const categoryData = Object.entries(data.averageCategoryScores).map(([name, value]) => ({ name, value }));
    const distributionData = Object.entries(data.opcLevelDistribution || {}).map(([name, value]) => ({ name, value }));
    const trendData = Object.entries(data.trendOverTime || {}).map(([name, value]) => ({ name, value }));
    const criticalCount = (data.criticalCategories || []).length;
    const avgScore = categoryData.length > 0 ? (categoryData.reduce((acc, c: any) => acc + c.value, 0) / categoryData.length) : 0;

    const [activeTab, setActiveTab] = useState<'analytics' | 'schema'>('analytics');

    return (
        <div className="space-y-8 animate-in fade-in duration-700">
            {/* Tab Navigation */}
            <div className="flex space-x-1 bg-slate-800/50 p-1 rounded-xl border border-slate-700/50 w-fit mb-8">
                <button
                    onClick={() => setActiveTab('analytics')}
                    className={`px-6 py-2.5 rounded-lg text-sm font-bold transition-all flex items-center gap-2 ${activeTab === 'analytics'
                        ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-500/20'
                        : 'text-slate-400 hover:text-white hover:bg-white/5'
                        }`}
                >
                    <BarChart3 className="w-4 h-4" />
                    Inteligencia Operativa
                </button>
                <button
                    onClick={() => setActiveTab('schema')}
                    className={`px-6 py-2.5 rounded-lg text-sm font-bold transition-all flex items-center gap-2 ${activeTab === 'schema'
                        ? 'bg-emerald-600 text-white shadow-lg shadow-emerald-500/20'
                        : 'text-slate-400 hover:text-white hover:bg-white/5'
                        }`}
                >
                    <div className="w-4 h-4"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><ellipse cx="12" cy="5" rx="9" ry="3" /><path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3" /><path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" /></svg></div>
                    Diccionario de Datos
                </button>
            </div>

            {/* Content: Analytics */}
            {activeTab === 'analytics' && (
                <>
                    {/* Mensaje de Inteligencia Estratégica para el Cliente */}
                    <div className="bg-indigo-600/10 border border-indigo-500/20 p-8 rounded-3xl mb-8 relative overflow-hidden group">
                        <div className="absolute top-0 right-0 p-8 opacity-5 group-hover:opacity-10 transition-opacity">
                            <TrendingUp className="w-32 h-32 text-indigo-400" />
                        </div>
                        <div className="relative z-10">
                            <div className="flex items-center gap-3 mb-4">
                                <div className="bg-indigo-600 p-2 rounded-lg">
                                    <BarChart3 className="w-6 h-6 text-white" />
                                </div>
                                <h3 className="text-xl font-bold text-white">Inteligencia Policial: Reconocimiento de Patrones</h3>
                            </div>
                            <p className="text-indigo-200/80 leading-relaxed max-w-4xl">
                                Este módulo emplea algoritmos de análisis para identificar <span className="text-white font-bold">fallas sistémicas</span> en la coordinación.
                                VANTAK detecta automáticamente vulnerabilidades recurrentes en logística, protocolos o mando, transformando datos históricos en
                                <span className="text-white font-bold">inteligencia accionable</span> para el mando superior.
                            </p>
                        </div>
                    </div>

                    {/* KPI Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div className="bg-slate-800/50 border border-slate-700 p-6 rounded-2xl">
                            <div className="flex justify-between items-start">
                                <div>
                                    <p className="text-slate-400 text-sm font-medium">Categorías Críticas</p>
                                    <h4 className="text-3xl font-bold mt-1 text-red-400">{criticalCount}</h4>
                                </div>
                                <div className="p-3 bg-red-400/10 rounded-xl">
                                    <AlertTriangle className="w-6 h-6 text-red-400" />
                                </div>
                            </div>
                            <p className="text-xs text-slate-500 mt-4 leading-relaxed">
                                Detectadas áreas con desempeño inferior al 70%. Requieren atención inmediata.
                            </p>
                        </div>

                        <div className="bg-slate-800/50 border border-slate-700 p-6 rounded-2xl">
                            <div className="flex justify-between items-start">
                                <div>
                                    <p className="text-slate-400 text-sm font-medium">Score Promedio Global</p>
                                    <h4 className="text-3xl font-bold mt-1 text-indigo-400">{avgScore.toFixed(1)}%</h4>
                                </div>
                                <div className="p-3 bg-indigo-400/10 rounded-xl">
                                    <TrendingUp className="w-6 h-6 text-indigo-400" />
                                </div>
                            </div>
                            <p className="text-xs text-slate-500 mt-4 leading-relaxed">
                                Índice de madurez operativa actual basado en todas las evaluaciones históricas.
                            </p>
                        </div>

                        <div className="bg-slate-800/50 border border-slate-700 p-6 rounded-2xl">
                            <div className="flex justify-between items-start">
                                <div>
                                    <p className="text-slate-400 text-sm font-medium">Estado de Coordinación</p>
                                    <h4 className={`text-3xl font-bold mt-1 ${criticalCount > 0 ? 'text-amber-400' : 'text-emerald-400'}`}>
                                        {criticalCount > 0 ? 'En Alerta' : 'Óptimo'}
                                    </h4>
                                </div>
                                <div className={`p-3 rounded-xl ${criticalCount > 0 ? 'bg-amber-400/10' : 'bg-emerald-400/10'}`}>
                                    <CheckCircle className={`w-6 h-6 ${criticalCount > 0 ? 'text-amber-400' : 'text-emerald-400'}`} />
                                </div>
                            </div>
                            <p className="text-xs text-slate-500 mt-4 leading-relaxed">
                                {criticalCount > 0 ? 'Se han detectado patrones de falla que requieren intervención.' : 'La coordinación entre agencias es fluida y coherente.'}
                            </p>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                        {/* Chart: Category Performance */}
                        <div className="bg-slate-800/50 border border-slate-700 p-8 rounded-3xl">
                            <h3 className="text-lg font-semibold mb-6 flex items-center gap-2 text-slate-200">
                                <BarChart3 className="w-5 h-5 text-indigo-400" />
                                Desempeño por Categoría Estratégica
                            </h3>
                            <div className="h-[300px] w-full">
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={categoryData} layout="vertical" margin={{ left: 40, right: 20 }}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#334155" horizontal={true} vertical={false} />
                                        <XAxis type="number" hide domain={[0, 100]} />
                                        <YAxis dataKey="name" type="category" stroke="#94a3b8" fontSize={12} width={120} />
                                        <Tooltip
                                            contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: '8px' }}
                                            itemStyle={{ color: '#fff' }}
                                        />
                                        <Bar dataKey="value" fill="#6366f1" radius={[0, 4, 4, 0]} barSize={20} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </div>

                        {/* Chart: OPC Level Distribution */}
                        <div className="bg-slate-800/50 border border-slate-700 p-8 rounded-3xl">
                            <h3 className="text-lg font-semibold mb-6 text-slate-200">Distribución de Niveles OPC</h3>
                            <div className="h-[300px] w-full flex items-center">
                                <ResponsiveContainer width="100%" height="100%">
                                    <PieChart>
                                        <Pie
                                            data={distributionData}
                                            cx="50%"
                                            cy="40%"
                                            innerRadius={60}
                                            outerRadius={90}
                                            paddingAngle={5}
                                            dataKey="value"
                                        >
                                            {distributionData.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={LEVEL_COLORS[entry.name] || COLORS[index % COLORS.length]} />
                                            ))}
                                        </Pie>
                                        <Tooltip
                                            contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: '8px' }}
                                            itemStyle={{ color: '#fff' }}
                                        />
                                        <Legend verticalAlign="bottom" height={36} />
                                    </PieChart>
                                </ResponsiveContainer>
                            </div>
                        </div>

                        {/* Chart: Trend Over Time */}
                        <div className="bg-slate-800/50 border border-slate-700 p-8 rounded-3xl lg:col-span-2">
                            <h3 className="text-lg font-semibold mb-6 text-slate-200">Evolución del Score de Coordinación</h3>
                            <div className="h-[300px] w-full">
                                <ResponsiveContainer width="100%" height="100%">
                                    <LineChart data={trendData}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#334155" vertical={false} />
                                        <XAxis dataKey="name" stroke="#94a3b8" fontSize={12} />
                                        <YAxis stroke="#94a3b8" fontSize={12} domain={[0, 100]} />
                                        <Tooltip
                                            contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: '8px' }}
                                            itemStyle={{ color: '#fff' }}
                                        />
                                        <Line type="monotone" dataKey="value" stroke="#8b5cf6" strokeWidth={3} dot={{ fill: '#8b5cf6', strokeWidth: 2, r: 4 }} activeDot={{ r: 6 }} />
                                    </LineChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                    </div>

                    {/* Critical Areas Table */}
                    {criticalCount > 0 && (
                        <div className="bg-red-950/20 border border-red-500/20 p-8 rounded-3xl">
                            <h3 className="text-lg font-semibold text-red-400 mb-4 flex items-center gap-2">
                                <AlertTriangle className="w-5 h-5" />
                                Alertas de Patrón Crítico
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                {data.criticalCategories.map((cat: any, i: number) => (
                                    <div key={i} className="bg-slate-900/50 p-4 rounded-xl border border-red-500/10">
                                        <div className="flex justify-between items-center mb-2">
                                            <span className="text-sm font-medium text-slate-300">{cat.category}</span>
                                            <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${cat.status === 'CRITICAL' ? 'bg-red-500/20 text-red-400' : 'bg-amber-500/20 text-amber-400'}`}>
                                                {cat.averageScore.toFixed(1)}%
                                            </span>
                                        </div>
                                        <p className="text-xs text-slate-500 italic">
                                            Patrón detectado: {cat.status === 'CRITICAL' ? 'Falla estructural recurrente.' : 'Requiere refuerzo preventivo.'}
                                        </p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </>
            )}

            {/* Content: SchemaSpy */}
            {activeTab === 'schema' && (
                <div className="bg-white rounded-3xl overflow-hidden shadow-2xl h-[800px] border border-slate-700/50 relative">
                    <iframe
                        src="/schemaspy/index.html"
                        className="w-full h-full"
                        title="Database Schema"
                        onError={(e) => console.error("Error loading schema", e)}
                    />

                    {/* Overlay informativo de fallback (se muestra detrás si el iframe carga transparente o falla 404 en algunos casos, aunque mejor sería manejar el error de carga real) */}
                    <div className="absolute -z-10 inset-0 flex flex-col items-center justify-center bg-slate-900 text-slate-400 p-10 text-center">
                        <div className="p-4 bg-slate-800 rounded-full mb-4">
                            <AlertTriangle className="w-8 h-8 text-amber-500" />
                        </div>
                        <h3 className="text-xl font-bold text-white mb-2">Documentación no generada</h3>
                        <p className="max-w-md mb-6">
                            No se encontró el reporte de SchemaSpy. Ejecute el script de generación en el servidor para analizar la base de datos.
                        </p>
                        <code className="bg-black/50 px-4 py-2 rounded-lg text-sm text-green-400 font-mono">
                            ./generate_schemaspy.sh
                        </code>
                    </div>
                </div>
            )}
        </div>
    );
};

export default EvaluationAnalytics;
